import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { answer_voted } from '@/analytics';
import { EAnswerVote } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useAnswerUpvote_UpvoteAnswer,
  useAnswerUpvote_UpvoteAnswerVariables,
} from './graphql-types/useAnswerUpvote_UpvoteAnswer';
import { useAnswerVote_AnswerFragment } from './graphql-types/useAnswerVote_AnswerFragment';

const ANSWER_FRAGMENT = gql`
  fragment useAnswerVote_AnswerFragment on AnswerV2 {
    _id
    currentUserVote
    questionPost {
      _id
    }
    tags {
      _id
    }
    announcement {
      _id
    }
    sponsorData {
      audience {
        group {
          _id
        }
      }
    }
    group {
      _id
      isGlobalSingleton
    }
    organization {
      _id
    }
    event {
      _id
    }
  }
`;

const VOTE = gql`
  mutation useAnswerUpvote_UpvoteAnswer($postId: String!, $vote: EAnswerVote!) {
    modifyVoteOnAnswer(input: { answerPostId: $postId, vote: $vote }) {
      result {
        numUpvotes
        numDownvotes
        ...useAnswerVote_AnswerFragment
      }
    }
  }
  ${ANSWER_FRAGMENT}
`;
export const useVoteButton: {
  (answer: useAnswerVote_AnswerFragment): {
    handleVote: (answerVote: EAnswerVote) => Promise<void>;
  };
  answerFragment: DocumentNode;
} = (
  answer: useAnswerVote_AnswerFragment
): {
  handleVote: (answerVote: EAnswerVote) => Promise<void>;
} => {
  const toast = useToast();
  const [vote] = useMutation<
    useAnswerUpvote_UpvoteAnswer,
    useAnswerUpvote_UpvoteAnswerVariables
  >(VOTE);

  const handleVote = async (answerVote: EAnswerVote): Promise<void> => {
    try {
      await vote({
        variables: {
          postId: answer._id,
          vote:
            answerVote === answer.currentUserVote
              ? EAnswerVote.NONE
              : answerVote,
        },
      });
      if (
        answerVote !== answer.currentUserVote &&
        (answerVote === EAnswerVote.UPVOTE ||
          answerVote === EAnswerVote.DOWNVOTE)
      ) {
        answer_voted({
          entity_type: 'answer',
          entity_id: answer._id,
          answer_question_id: (answer as any)?.questionPost?._id || answer._id,
          answer_vote_direction:
            answerVote === EAnswerVote.UPVOTE ? 'upvote' : 'downvote',
          content_has_tags: !!answer?.tags && answer.tags.length > 0,
          post_is_announcement: !!answer?.announcement?._id || false,
          location_posted_in: answer?.event
            ? 'event'
            : answer?.organization
            ? 'organization'
            : answer?.group?.isGlobalSingleton
            ? 'general'
            : 'group',
          location_group_id: answer?.group?.isGlobalSingleton
            ? undefined
            : answer?.group?._id,
          location_organization_id: answer?.organization?._id,
          location_event_id: answer?.event?._id,
          is_hosted_by_sponsor: !!answer?.sponsorData?.audience.group,
          post_type: 'answer',
          reaction_type:
            answerVote === EAnswerVote.UPVOTE ? 'upvote' : 'downvote',
        });
      }
    } catch (err) {
      logError(err);
      toast({
        description: 'Failed to update vote on answer',
        status: 'error',
      });
    }
  };
  return {
    handleVote,
  };
};

useVoteButton.answerFragment = ANSWER_FRAGMENT;
